/*
  Exibição real dos cards de produto e clique no card, direto para o GA4.

  - Conta um card quando ≥ 50% dele fica na tela por ≥ 1 s; uma vez por
    produto e lista em cada página. Vai em lote como `view_item_list` com a
    lista "Tela | <lista>". O `view_item_list` do app Shopify conta o que
    carrega, não o que aparece: filtre pelo prefixo "Tela | ".
  - Clique na foto ou no nome do card: `select_item`.
  - INP de campo: o toque mais lento da página, se ≥ 200 ms, vai como
    `inp_lento` ao sair da página, com o elemento, o script que mais pesou
    (long animation frame) e se foi durante o carregamento.

  Os cards vêm de snippets/product-card-carraro.liquid com data-medicao-*;
  a lista e a posição são passadas por cada seção.
  Depende de window.CarraroMedicao (snippets/medicao-carraro.liquid).
*/
(function () {
  const medicao = window.CarraroMedicao;
  if (!medicao || !('IntersectionObserver' in window)) return;

  const LIMIAR = 0.5;
  const TEMPO_MS = 1000;
  const LOTE_MAX = 12;
  const LOTE_ESPERA_MS = 2000;
  const PREFIXO = 'Tela | ';
  const SELETOR = '[data-medicao-card]';

  /** @type {Set<string>} lista + item já contados nesta página */
  const contados = new Set();
  /** @type {Map<string, object[]>} itens à espera de envio, por lista */
  const fila = new Map();
  /** @type {WeakMap<Element, number>} temporizador de 1 s por card */
  const temporizadores = new WeakMap();
  let loteTimer = 0;

  function item(card) {
    const ds = card.dataset;
    const lista = PREFIXO + (ds.medicaoLista || 'Outros');
    const dados = {
      item_id: ds.itemId,
      item_name: ds.itemName,
      item_brand: ds.itemBrand || undefined,
      item_category: ds.itemCategory || undefined,
      item_variant: ds.itemVariant || undefined,
      price: Number(ds.itemPrice) || 0,
      quantity: 1,
      item_list_name: lista,
    };
    const posicao = Number(ds.medicaoPosicao);
    if (posicao > 0) dados.index = posicao;
    return dados;
  }

  // modo: padrão = envia depois da pintura; 'ja' = aba oculta;
  // 'guardar' = saindo da página (vai na próxima, ver CarraroMedicao.guardar).
  function enviarLote(modo) {
    clearTimeout(loteTimer);
    loteTimer = 0;
    fila.forEach((itens, lista) => {
      for (let i = 0; i < itens.length; i += LOTE_MAX) {
        const params = { item_list_name: lista, items: itens.slice(i, i + LOTE_MAX) };
        if (modo === 'guardar') medicao.guardar('view_item_list', params);
        else medicao.enviar('view_item_list', params, modo);
      }
    });
    fila.clear();
  }

  function contar(card) {
    const dados = item(card);
    if (!dados.item_id) return;
    const chave = dados.item_list_name + '|' + dados.item_id;
    if (contados.has(chave)) return;
    contados.add(chave);
    observador.unobserve(card);

    const itens = fila.get(dados.item_list_name) || [];
    itens.push(dados);
    fila.set(dados.item_list_name, itens);
    if (itens.length >= LOTE_MAX) enviarLote();
    else if (!loteTimer) loteTimer = setTimeout(() => enviarLote(), LOTE_ESPERA_MS);
  }

  const observador = new IntersectionObserver(
    (entradas) => {
      for (const entrada of entradas) {
        const card = entrada.target;
        if (entrada.isIntersecting && entrada.intersectionRatio >= LIMIAR) {
          if (!temporizadores.has(card)) {
            temporizadores.set(card, setTimeout(() => {
              temporizadores.delete(card);
              contar(card);
            }, TEMPO_MS));
          }
        } else if (temporizadores.has(card)) {
          clearTimeout(temporizadores.get(card));
          temporizadores.delete(card);
        }
      }
    },
    { threshold: [LIMIAR] }
  );

  function observar(raiz) {
    const cards = raiz.matches?.(SELETOR) ? [raiz] : raiz.querySelectorAll?.(SELETOR) || [];
    for (const card of cards) {
      if (card.dataset.medicaoObservado) continue;
      card.dataset.medicaoObservado = '1';
      observador.observe(card);
    }
  }

  // Cards que chegam depois (rolagem infinita, recomendações, filtros):
  // só junta os nós novos e varre quando o navegador estiver livre.
  const novos = new Set();
  let varreduraAgendada = false;
  const agendar = window.requestIdleCallback
    ? (fn) => requestIdleCallback(fn, { timeout: 1000 })
    : (fn) => setTimeout(fn, 200);

  new MutationObserver((mutacoes) => {
    for (const m of mutacoes) {
      for (const no of m.addedNodes) if (no.nodeType === 1) novos.add(no);
    }
    if (!novos.size || varreduraAgendada) return;
    varreduraAgendada = true;
    agendar(() => {
      varreduraAgendada = false;
      for (const no of novos) if (no.isConnected) observar(no);
      novos.clear();
    });
  }).observe(document.body, { childList: true, subtree: true });

  observar(document);

  // Clique no card: foto ou nome. Abre outra página, então fica guardado e
  // vai no carregamento da próxima (ver CarraroMedicao.guardar).
  document.addEventListener('click', (evento) => {
    const alvo = evento.target;
    if (!(alvo instanceof Element)) return;
    // O coração de favoritos fica dentro do link da foto e não abre o produto.
    if (alvo.closest('button, [role="button"]')) return;
    const link = alvo.closest('a[href]');
    const card = link && link.closest(SELETOR);
    if (!card) return;
    const dados = item(card);
    if (!dados.item_id) return;
    medicao.guardar('select_item', { item_list_name: dados.item_list_name, items: [dados] });
  }, { capture: true, passive: true });

  // ---------- INP de campo ----------
  const INP_LIMIAR = 200;
  const tipos = PerformanceObserver.supportedEntryTypes || [];
  /** @type {{ e: PerformanceEventTiming, alvo: string } | null} */
  let pior = null;
  /** @type {any[]} quadros longos recentes (até 60) */
  const quadros = [];
  let inpEnviado = false;

  // "div.card-content > a.product-card__title-link", até 100 caracteres.
  function descrever(el) {
    const um = (n) => {
      if (!(n instanceof Element)) return '';
      const classes = (n.getAttribute('class') || '').trim().split(/\s+/).filter(Boolean).slice(0, 2);
      return n.tagName.toLowerCase() + (n.id ? '#' + n.id : '') + (classes.length ? '.' + classes.join('.') : '');
    };
    const pai = el instanceof Element ? um(el.parentElement) : '';
    return ((pai ? pai + ' > ' : '') + um(el)).slice(0, 100);
  }

  function nomeArquivo(url) {
    try {
      const u = new URL(url);
      const arq = u.pathname.split('/').pop() || u.pathname;
      return (u.host === location.host || u.host.endsWith('shopify.com') ? '' : u.host + '/') + arq;
    } catch (_) {
      return String(url || '').slice(0, 60);
    }
  }

  function enviarInp(modo) {
    if (inpEnviado || !pior || pior.e.duration < INP_LIMIAR) return;
    inpEnviado = true;
    const e = pior.e;
    const fim = e.startTime + e.duration;
    let script = '';
    let scriptMs = 0;
    for (const q of quadros) {
      if (q.startTime > fim || q.startTime + q.duration < e.startTime) continue;
      for (const s of q.scripts || []) {
        if (s.duration <= scriptMs) continue;
        scriptMs = s.duration;
        script = (nomeArquivo(s.sourceURL) || s.invoker || '?') + (s.sourceFunctionName ? ' :: ' + s.sourceFunctionName : '');
      }
    }
    const nav = performance.getEntriesByType('navigation')[0];
    const carregou = nav && nav.loadEventEnd > 0 ? nav.loadEventEnd : Infinity;
    const params = {
      inp_valor: Math.round(e.duration),
      inp_atraso: Math.round(e.processingStart - e.startTime),
      inp_processamento: Math.round(e.processingEnd - e.processingStart),
      inp_apresentacao: Math.max(0, Math.round(fim - e.processingEnd)),
      inp_tipo: e.name,
      inp_alvo: pior.alvo,
      inp_script: script.slice(0, 100),
      inp_script_ms: Math.round(scriptMs),
      inp_momento: e.startTime < carregou ? 'carregando' : 'carregado',
      tipo_pagina: medicao.pagina || '',
    };
    if (modo === 'guardar') medicao.guardar('inp_lento', params);
    else medicao.enviar('inp_lento', params, 'ja');
  }

  if (tipos.includes('event')) {
    new PerformanceObserver((lista) => {
      for (const e of /** @type {PerformanceEventTiming[]} */ (lista.getEntries())) {
        if (!e.interactionId) continue;
        if (!pior || e.duration > pior.e.duration) pior = { e, alvo: descrever(e.target) };
      }
    }).observe({ type: 'event', buffered: true, durationThreshold: 40 });

    if (tipos.includes('long-animation-frame')) {
      new PerformanceObserver((lista) => {
        for (const q of lista.getEntries()) {
          quadros.push(q);
          if (quadros.length > 60) quadros.shift();
        }
      }).observe({ type: 'long-animation-frame', buffered: true });
    }
  }

  // Saindo da página (pagehide vem antes do visibilitychange): guarda para a
  // próxima. Só a aba oculta (troca de aba, app em segundo plano): envia já.
  addEventListener('pagehide', () => {
    enviarLote('guardar');
    enviarInp('guardar');
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'hidden') return;
    enviarLote('ja');
    enviarInp('ja');
  });
})();
